/**
 * JFK Store — Storefront page JS
 *
 * Config is injected by Liquid into window.JFKStore before this file loads.
 *
 * Sections:
 *   1. Config
 *   2. Utilities
 *   3. API — Node.js store fetch + Shopify Storefront GraphQL
 *   4. Render — product cards, option dropdowns
 *   5. Product Groups UI — All / per-group selection
 *   6. Dropdown UI — custom selectable dropdowns
 *   7. Modal — image gallery + product detail
 *   8. SEO — dynamic title + meta tags
 *   9. Boot
 */

(function () {
  'use strict';

  /* ══════════════════════════════════════════════════════════════════════
     1. CONFIG  (injected by Liquid)
  ══════════════════════════════════════════════════════════════════════ */

  var cfg = window.JFKStore || {};

  var API_BASE           = cfg.apiBase           || '';
  var SHOP_NAME          = cfg.shopName          || '';
  var STOREFRONT_TOKEN   = cfg.storefrontToken   || '';
  var STOREFRONT_API_URL = 'https://' + (cfg.shopDomain || '') + '/api/2025-01/graphql.json';
  var FALLBACK_IMAGE     = cfg.fallbackImage     || '';

  /* State */
  var storeProductGroups  = [];   // raw productgroups from Node.js API
  var shopifyCache        = {};   // keyed 'v_{variantId}' or 'p_{productId}'
  var nodeGroupMap        = {};   // nodeId → groupTitle
  var nodeAllowedVariants = {};   // productGid → [variantNumericId, ...] or null (= all)
  var currentStoreId      = '';
  var currentStoreName    = '';
  var currentNodeId       = '';   // node ID open in modal
  var currentModalVariants = [];  // filtered variant edges for the open modal
  var currentSearchQuery  = '';   // active client-side search term


  /* ══════════════════════════════════════════════════════════════════════
     2. UTILITIES
  ══════════════════════════════════════════════════════════════════════ */

  function toPascalCase(str) {
    return (str || '').replace(/\w\S*/g, function (w) {
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    });
  }

  function el(id) { return document.getElementById(id); }

  function setMeta(id, content) {
    var node = el(id);
    if (node) node.setAttribute('content', content);
  }

  function showEl(id)  { var n = el(id); if (n) n.style.display = 'block'; }
  function hideEl(id)  { var n = el(id); if (n) n.style.display = 'none';  }

  function resolveLogoUrl(logo) {
    if (!logo) return null;
    if (/^https?:\/\//i.test(logo)) return logo;
    var origin = API_BASE.split('/').slice(0, 3).join('/');
    return origin + (logo.charAt(0) === '/' ? '' : '/') + logo;
  }

  function isActive(item) {
    return item.status === 1 || item.status === '1';
  }


  /* ══════════════════════════════════════════════════════════════════════
     3. API
  ══════════════════════════════════════════════════════════════════════ */

  function fetchStore(storeId) {
    return fetch(API_BASE + '/' + storeId)
      .then(function (res) { return res.json(); });
  }

  var STOREFRONT_QUERY = [
    'query getNodes($ids:[ID!]!){',
    '  nodes(ids:$ids){',
    '    __typename',
    '    ...on Product{',
    '      id title description availableForSale tags productType vendor',
    '      collections(first:10){edges{node{id}}}',
    '      featuredImage{url}',
    '      images(first:10){edges{node{url altText}}}',
    '      priceRange{minVariantPrice{amount currencyCode}}',
    '      options{name values}',
    '      variants(first:250){edges{node{id title sku availableForSale quantityAvailable image{url} selectedOptions{name value}}}}',
    '    }',
    '    ...on ProductVariant{',
    '      id title sku availableForSale quantityAvailable',
    '      price{amount currencyCode}',
    '      image{url}',
    '      selectedOptions{name value}',
    '      product{',
    '        id title description tags productType vendor',
    '        collections(first:10){edges{node{id}}}',
    '        featuredImage{url}',
    '        images(first:10){edges{node{url altText}}}',
    '        options{name values}',
    '        variants(first:250){edges{node{id title sku availableForSale quantityAvailable image{url} selectedOptions{name value}}}}',
    '      }',
    '    }',
    '    ...on Page{',
    '      id title body',
    '    }',
    '  }',
    '}'
  ].join('');


  function fetchStorefrontNodes(gids) {
    // console.log('[JFK] Fetching Storefront nodes:', gids);
    return fetch(STOREFRONT_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
      },
      body: JSON.stringify({ query: STOREFRONT_QUERY, variables: { ids: gids } })
    })
    .then(function (res) { return res.json(); })
    .then(function (json) {
      // console.log('[JFK] FULL Storefront API Response:', JSON.stringify(json, null, 2));
      var nodes = (json.data && json.data.nodes) ? json.data.nodes : [];
      
      nodes.forEach(function (node, i) {
        if (!node) {
          console.error('[JFK] Node at index ' + i + ' (GID: ' + gids[i] + ') is NULL. Is it published to the Storefront channel?');
          return;
        }
        var decodedId = node.id;
        if (node.id.indexOf('gid://') === -1) {
          try { decodedId = atob(node.id); } catch(e) {}
        }
        var key = decodedId
          .replace('gid://shopify/ProductVariant/', 'v_')
          .replace('gid://shopify/Product/', 'p_')
          .replace('gid://shopify/Page/', 'page_');
        shopifyCache[key] = node;
      });
      return nodes; // Return all, including nulls for matching logic
    });
  }

  function loadHeaderFooter(settings) {
    // console.log('[JFK] loadHeaderFooter settings:', settings);
    if (!settings) return;

    var headerId = settings['store.justforkix.com-storefront-header'];
    var footerId = settings['store.justforkix.com-storefront-footer'];
    var bodyId   = settings['store.justforkix.com-homepage-body'];

    var headerEl = el('shopify-section-jfk-store-header');
    var footerEl = el('shopify-section-jfk-store-footer');
    var bodyEl   = el('jfk-store-homepage-body');

    // Show temporary loading text
    if (headerId && headerEl) headerEl.innerHTML = '<div style="text-align:center;padding:20px;opacity:0.5;font-size:12px;">Loading dynamic header...</div>';
    if (footerId && footerEl) footerEl.innerHTML = '<div style="text-align:center;padding:20px;opacity:0.5;font-size:12px;">Loading dynamic footer...</div>';

    var gids = [];
    if (headerId) gids.push('gid://shopify/Page/' + headerId);
    if (footerId) gids.push('gid://shopify/Page/' + footerId);
    if (bodyId)   gids.push('gid://shopify/Page/' + bodyId);

    if (!gids.length) return;

    fetchStorefrontNodes(gids).then(function (nodes) {
      var headerFound = false;
      var footerFound = false;

      nodes.forEach(function (node, i) {
        if (!node) return;

        if (node.__typename === 'Page') {
          var nodeNumericId = getNumericId(node.id);
          var nodeGid       = node.id;
          var nodeBody      = node.body || '';
          
          // Match Header
          if (headerId && (String(nodeNumericId) === String(headerId) || nodeGid.indexOf(String(headerId)) !== -1)) {
            headerFound = true;
            if (headerEl) {
              if (nodeBody.length > 0) {
                headerEl.innerHTML = nodeBody;
                // hideEl('jfk-default-header');
              } else {
                console.warn('[JFK] Header page found but body is empty.');
                headerEl.innerHTML = ''; 
              }
            }
          }
          // Match Footer
          if (footerId && (String(nodeNumericId) === String(footerId) || nodeGid.indexOf(String(footerId)) !== -1)) {
            footerFound = true;
            if (footerEl) {
              footerEl.innerHTML = nodeBody;
            }
          }
          // Match Body
          if (bodyId && (String(nodeNumericId) === String(bodyId) || nodeGid.indexOf(String(bodyId)) !== -1)) {
            if (bodyEl) {
              if (nodeBody.length > 0) {
                bodyEl.innerHTML = nodeBody;
                showEl('jfk-store-homepage-body');
                hideEl('jfk-store-description');
              }
            }
          }
        }
      });

      // If requested but not found, clear the "Loading..." text
      if (headerId && !headerFound && headerEl) {
        console.error('[JFK] Header page (ID: ' + headerId + ') was not found or is unpublished.');
        headerEl.innerHTML = ''; 
      }
      if (footerId && !footerFound && footerEl) {
        console.error('[JFK] Footer page (ID: ' + footerId + ') was not found or is unpublished.');
        footerEl.innerHTML = '';
      }

    }).catch(function(err) {
      console.error('[JFK] Error loading dynamic content:', err);
      if (headerEl) headerEl.innerHTML = '';
      if (footerEl) footerEl.innerHTML = '';
    });
  }


  /* ══════════════════════════════════════════════════════════════════════
     4. RENDER
  ══════════════════════════════════════════════════════════════════════ */

  /**
   * Build custom styled dropdowns from [{name, values[]}]
   */
  function buildOptionDropdowns(options) {
    return options.map(function (opt) {
      var items = opt.values.map(function (val, i) {
        return '<div class="jfk-opt-item' + (i === 0 ? ' selected' : '') + '" data-value="' + val + '">'
          + val + '</div>';
      }).join('');
      return '<div class="jfk-opt-dropdown">'
        + '<div class="jfk-opt-toggle" data-name="' + opt.name + '">'
        + '<span class="jfk-opt-label">' + opt.name + ': ' + opt.values[0] + '</span>'
        + '</div>'
        + '<div class="jfk-opt-list">' + items + '</div>'
        + '</div>';
    }).join('');
  }

  //storefront-color-image-issue
  /**
   * Filter variants of a Product node according to nodeAllowedVariants.
   * If specific variant IDs were saved by the store owner, only variants matching
   * those selected colors (or IDs) will be returned.
   */
  function getFilteredVariants(node) {
    if (!node) return [];
    if (node.__typename === 'ProductVariant') return [{ node: node }];
    var allVariants = (node.variants && node.variants.edges) || [];
    var allowedIds  = nodeAllowedVariants[node.id];
    if (!allowedIds || !allowedIds.length) return allVariants;

    // Find which color(s) were originally selected, from the stored variant_ids
    var allowedColors = {};
    allVariants.forEach(function (e) {
      if (allowedIds.indexOf(getNumericId(e.node.id)) !== -1) {
        (e.node.selectedOptions || []).forEach(function (o) {
          if (o.name.toLowerCase() === 'color') allowedColors[o.value] = true;
        });
      }
    });
    var hasColorOption = Object.keys(allowedColors).length > 0;

    return hasColorOption
      ? allVariants.filter(function (e) {
          return (e.node.selectedOptions || []).some(function (o) {
            return o.name.toLowerCase() === 'color' && allowedColors[o.value];
          });
        })
      : allVariants.filter(function (e) {
          return allowedIds.indexOf(getNumericId(e.node.id)) !== -1;
        });
  }
  //storefront-color-image-issue

  /**
   * Render a single Shopify node (Product or ProductVariant) as a product card.
   */
  function renderNode(node, collectionTitle) {
    var name, img, price;

    if (node.__typename === 'ProductVariant') {
      name  = (node.product && node.product.title) ? node.product.title : '';
      img   = (node.image && node.image.url) ? node.image.url
              : (node.product && node.product.featuredImage) ? node.product.featuredImage.url
              : FALLBACK_IMAGE;
      price = node.price ? '$' + parseFloat(node.price.amount).toFixed(2) : '';
    } else {
      name  = node.title || '';
      //storefront-color-image-issue
      var filtered   = getFilteredVariants(node);
      var variantImg = (filtered.length && filtered[0].node.image) ? filtered[0].node.image.url : null;
      img   = variantImg || (node.featuredImage && node.featuredImage.url) || FALLBACK_IMAGE;
      //storefront-color-image-issue
      price = (node.priceRange && node.priceRange.minVariantPrice)
              ? '$' + parseFloat(node.priceRange.minVariantPrice.amount).toFixed(2) : '';
    }

    var brandLabel       = collectionTitle || '';
    var soldOut          = node.availableForSale === false;
    var soldOutBadge     = soldOut ? '<span class="jfk-sold-out">Sold Out</span>' : '';
    var shopifyProductId = node.__typename === 'ProductVariant'
      ? getNumericId((node.product && node.product.id) || node.id)
      : getNumericId(node.id);

    return '<div class="store-product-div" data-shopify-product-id="' + shopifyProductId + '">'
      + soldOutBadge
      + '<img src="' + img + '" loading="lazy" alt="' + name + '" class="jfk-product-thumb" data-node-id="' + node.id + '">'
      + '<div class="div-block-41">'
        + '<div class="product-title-div">'
          + '<div class="store-brand">' + brandLabel + '</div>'
          + '<div class="store-product-name">' + name + '</div>'
          + '<div class="store-price">' + price + '</div>'
        + '</div>'
        + '<a href="#" class="studio-store-add-button-copy w-button" data-node-id="' + node.id + '" aria-label="Add to cart">+</a>'
      + '</div>'
      + '</div>';
  }


  /* ══════════════════════════════════════════════════════════════════════
     5. PRODUCT GROUPS UI
  ══════════════════════════════════════════════════════════════════════ */

  /**
   * Returns [{gid, cacheKey, groupTitle}] for the given group ID.
   * Pass 'all' to get every product across all groups (deduplicated).
   * variant_ids can be null (→ all variants) or a JSON string / array
   * of variant IDs — in both cases we fetch the Product node and restrict
   * the modal options to only those variant IDs.
   */
  function parseVariantIds(raw) {
    if (!raw) return null;
    if (Array.isArray(raw)) return raw.length ? raw.map(String) : null;
    if (typeof raw === 'string') {
      try {
        var parsed = JSON.parse(raw);
        return Array.isArray(parsed) && parsed.length ? parsed.map(String) : null;
      } catch (e) { return null; }
    }
    return null;
  }

  function buildGidsForGroup(groupId) {
    var groups = groupId === 'all'
      ? storeProductGroups
      : storeProductGroups.filter(function (g) { return String(g.id) === String(groupId); });

    var seen = {};
    var result = [];

    groups.forEach(function (group) {
      (group.products || []).forEach(function (p) {
        var pid        = String(p.shopify_product_id);
        var key        = 'p_' + pid;
        var gid        = 'gid://shopify/Product/' + pid;
        var groupTitle = toPascalCase(group.title);
        var varIds     = parseVariantIds(p.variant_ids);

        if (!seen[key]) {
          seen[key] = true;
          // Record which variant IDs are allowed for this product (null = all)
          nodeAllowedVariants[gid] = varIds;
          result.push({ gid: gid, cacheKey: key, groupTitle: groupTitle });
        }
      });
    });

    return result;
  }

  function loadGroupProducts(groupId) {
    var grid = el('jfk-products-grid');
    var items = buildGidsForGroup(groupId);

    if (!items.length) {
      grid.innerHTML = '<p style="padding:40px;text-align:center;width:100%;">No products in this group yet.</p>';
      return;
    }

    var toFetch = items
      .filter(function (item) { return !shopifyCache[item.cacheKey]; })
      .map(function (item) { return item.gid; });

    grid.innerHTML = '<p style="padding:40px;text-align:center;width:100%;">Loading products&hellip;</p>';

    var ready = toFetch.length ? fetchStorefrontNodes(toFetch) : Promise.resolve([]);

    ready.then(function () {
      var rendered = items
        .map(function (item) {
          var node = shopifyCache[item.cacheKey];
          if (node) {
            nodeGroupMap[node.id] = item.groupTitle;
            return renderNode(node, item.groupTitle);
          }
          return null;
        })
        .filter(Boolean)
        .join('');

      grid.innerHTML = rendered
        || '<p style="padding:40px;text-align:center;width:100%;">No products found.</p>';

      initProductDropdowns(grid);
      if (currentSearchQuery) filterProducts(currentSearchQuery);
    }).catch(function () {
      grid.innerHTML = '<p style="padding:40px;text-align:center;width:100%;">Could not load products.</p>';
    });
  }

  function filterProducts(query) {
    var grid = el('jfk-products-grid');
    if (!grid) return;
    var q = (query || '').trim().toLowerCase();
    Array.from(grid.querySelectorAll('.store-product-div')).forEach(function (card) {
      if (!q) {
        card.style.display = '';
        return;
      }
      var name  = ((card.querySelector('.store-product-name') || {}).textContent || '').toLowerCase();
      var brand = ((card.querySelector('.store-brand') || {}).textContent || '').toLowerCase();
      card.style.display = (name.indexOf(q) !== -1 || brand.indexOf(q) !== -1) ? '' : 'none';
    });
  }

  function initProductGroups(groups) {
    var nav        = el('jfk-collections-list');
    var toggleText = el('jfk-collections-toggle-text');
    var dropdown   = nav ? nav.closest('.collections-dropdown') : null;

    if (!groups || groups.length === 0) {
      if (dropdown) dropdown.style.display = 'none';
      return;
    }
    if (dropdown) dropdown.style.display = '';

    // Build links — each product group
    var links = groups.map(function (group) {
      return '<a href="#" class="w-dropdown-link" data-group-id="' + group.id
        + '" data-group-title="' + toPascalCase(group.title) + '">'
        + toPascalCase(group.title) + '</a>';
    }).join('');
    nav.innerHTML = links;

    function selectLink(link, skipUrlUpdate) {
      Array.from(nav.querySelectorAll('.w-dropdown-link')).forEach(function (l) {
        l.classList.remove('w--current');
        l.style.fontWeight = '';
      });
      link.classList.add('w--current');
      link.style.fontWeight = '700';
      var title = link.getAttribute('data-group-title');
      toggleText.textContent = title;
      loadGroupProducts(link.getAttribute('data-group-id'));

      if (!skipUrlUpdate) {
        var params = new URLSearchParams(window.location.search);
        params.set('product_group', title.toLowerCase());
        var newUrl = window.location.pathname + '?' + params.toString();
        window.history.replaceState(null, '', newUrl);
      }
    }

    Array.from(nav.querySelectorAll('.w-dropdown-link')).forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        selectLink(link);
      });
    });

    // Default: select from URL or first available
    var urlParams = new URLSearchParams(window.location.search);
    var groupFromUrl = (urlParams.get('product_group') || '').toLowerCase();
    var targetLink;

    if (groupFromUrl) {
      targetLink = Array.from(nav.querySelectorAll('.w-dropdown-link')).find(function (l) {
        return l.getAttribute('data-group-title').toLowerCase() === groupFromUrl;
      });
    }

    if (!targetLink) {
      targetLink = nav.querySelector('.w-dropdown-link');
    }

    if (targetLink) {
      var isUrlMatch = targetLink.getAttribute('data-group-title').toLowerCase() === groupFromUrl;
      selectLink(targetLink, isUrlMatch);
    }
  }


  /* ══════════════════════════════════════════════════════════════════════
     6. DROPDOWN UI (product option dropdowns)
  ══════════════════════════════════════════════════════════════════════ */

  function initProductDropdowns(container) {
    Array.from(container.querySelectorAll('.jfk-opt-dropdown')).forEach(function (dropdown) {
      var toggle = dropdown.querySelector('.jfk-opt-toggle');
      var list   = dropdown.querySelector('.jfk-opt-list');
      var label  = dropdown.querySelector('.jfk-opt-label');

      toggle.addEventListener('click', function (e) {
        e.stopPropagation();
        var isOpen = list.classList.contains('open');
        Array.from(container.querySelectorAll('.jfk-opt-list.open')).forEach(function (l) {
          l.classList.remove('open');
        });
        if (!isOpen) list.classList.add('open');
      });

      Array.from(list.querySelectorAll('.jfk-opt-item')).forEach(function (item) {
        item.addEventListener('click', function (e) {
          e.stopPropagation();
          Array.from(list.querySelectorAll('.jfk-opt-item')).forEach(function (i) {
            i.classList.remove('selected');
          });
          item.classList.add('selected');
          label.textContent = toggle.getAttribute('data-name') + ': ' + item.getAttribute('data-value');
          list.classList.remove('open');
        });
      });
    });

    // Qty +/- buttons
    Array.from(container.querySelectorAll('.jfk-qty-btn')).forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var qtyDiv = btn.closest('.store-quantity').querySelector('.jfk-qty-value div');
        var val = parseInt(qtyDiv.textContent, 10) || 1;
        if (btn.getAttribute('data-action') === 'plus') {
          qtyDiv.textContent = val + 1;
        } else if (val > 1) {
          qtyDiv.textContent = val - 1;
        }
      });
    });

    // Image click → open modal
    Array.from(container.querySelectorAll('.jfk-product-thumb')).forEach(function (img) {
      img.addEventListener('click', function () {
        openProductModal(img.getAttribute('data-node-id'));
      });
    });

    // "+" quick-add button → open modal
    Array.from(container.querySelectorAll('.studio-store-add-button-copy')).forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        openProductModal(btn.getAttribute('data-node-id'));
      });
    });

    // Close option dropdowns on outside click
    document.addEventListener('click', function () {
      Array.from(container.querySelectorAll('.jfk-opt-list.open')).forEach(function (l) {
        l.classList.remove('open');
      });
    });
  }


  /* ══════════════════════════════════════════════════════════════════════
     7. MODAL
  ══════════════════════════════════════════════════════════════════════ */

  function updateVariantDisplay(node, detailsEl, variantEdges) {
    if (node.__typename === 'ProductVariant') return; // static for variant nodes
    var variantNumId = getSelectedVariantId(node);
    for (var i = 0; i < variantEdges.length; i++) {
      var v = variantEdges[i].node;
      if (getNumericId(v.id) !== variantNumId) continue;
      var skuEl   = detailsEl.querySelector('#jfk-modal-sku');
      var stockEl = detailsEl.querySelector('#jfk-modal-stock');
      if (skuEl && v.sku)   skuEl.textContent = 'SKU: ' + v.sku;
      if (stockEl) {
        stockEl.textContent = v.quantityAvailable != null
          ? 'Stock: only ' + v.quantityAvailable + ' left in stock'
          : '';
        stockEl.style.display = v.quantityAvailable != null ? '' : 'none';
      }
      return;
    }
  }

  function openProductModal(nodeId) {
    var node = null;
    Object.keys(shopifyCache).forEach(function (k) {
      if (shopifyCache[k] && shopifyCache[k].id === nodeId) node = shopifyCache[k];
    });
    if (!node) return;

    var isVariant   = node.__typename === 'ProductVariant';
    var clickProductId = isVariant
      ? getNumericId((node.product && node.product.id) || node.id)
      : getNumericId(node.id);
    var analyticsBase = API_BASE.replace(/\/stores\/?$/, '');
    fetch(analyticsBase + '/analytics/product-click', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        store_id:           currentStoreId,
        shopify_product_id: clickProductId,
        session_id:         window.JFK_SESSION || null
      }),
      keepalive: true
    }).catch(function () {});
    var title       = isVariant ? (node.product && node.product.title) : node.title;
    var description = isVariant ? (node.product && node.product.description) : node.description;
    var price       = isVariant
      ? (node.price ? '$' + parseFloat(node.price.amount).toFixed(2) : '')
      : (node.priceRange && node.priceRange.minVariantPrice
          ? '$' + parseFloat(node.priceRange.minVariantPrice.amount).toFixed(2) : '');
    
    //storefront-color-image-issue
    var allVariants      = (node.variants && node.variants.edges) || [];
    var allowedIds       = nodeAllowedVariants[nodeId]; // null = all, array = restricted
    var filteredVariants = getFilteredVariants(node);

    //storefront-color-image-issue
    // Expose filtered variants so getSelectedVariantId uses the right set
    currentModalVariants = filteredVariants;

    // Build color→image map from filtered variants only
    var colorImageMap = {};
    filteredVariants.forEach(function (e) {
      var v = e.node;
      (v.selectedOptions || []).forEach(function (o) {
        if (o.name.toLowerCase() === 'color' && v.image && v.image.url && !colorImageMap[o.value]) {
          colorImageMap[o.value] = v.image.url;
        }
      });
    });

    // Collect available option values from filtered variants (preserves order)
    var allowedOptValues = {};
    filteredVariants.forEach(function (e) {
      (e.node.selectedOptions || []).forEach(function (o) {
        if (!allowedOptValues[o.name]) allowedOptValues[o.name] = [];
        if (allowedOptValues[o.name].indexOf(o.value) === -1) allowedOptValues[o.name].push(o.value);
      });
    });

    //storefront-color-image-issue
    // Collect all images (for restricted variants, show only selected variant images)
    var allImages = [];
    if (isVariant) {
      if (node.image && node.image.url) allImages.push(node.image.url);
      var pImgs = (node.product && node.product.images && node.product.images.edges) || [];
      pImgs.forEach(function (e) {
        if (e.node.url && allImages.indexOf(e.node.url) === -1) allImages.push(e.node.url);
      });
    } else if (allowedIds) {
      filteredVariants.forEach(function (e) {
        var u = e.node.image && e.node.image.url;
        if (u && allImages.indexOf(u) === -1) allImages.push(u);
      });
    } else {
      var imgs = (node.images && node.images.edges) || [];
      imgs.forEach(function (e) { allImages.push(e.node.url); });
    }
    if (!allImages.length && node.featuredImage) allImages.push(node.featuredImage.url);
    if (!allImages.length) allImages.push(FALLBACK_IMAGE);
    //storefront-color-image-issue

    // Determine initial SKU from first filtered variant
    var initialSku = isVariant
      ? (node.sku || '')
      : (filteredVariants.length ? (filteredVariants[0].node.sku || '') : '');

    // Build options HTML — Color → image swatches, everything else → text-block-6
    // Only show values present in the allowed/filtered variants
    var optsHtml = '';
    if (isVariant) {
      var sopts = Array.isArray(node.selectedOptions) ? node.selectedOptions : [];
      sopts.forEach(function (o) {
        var isColor = o.name.toLowerCase() === 'color';
        optsHtml += '<div class="detail-page-color">' + o.name + ':</div><div class="colors-div">';
        if (isColor) {
          var swatchImg = (node.image && node.image.url) ? node.image.url : '';
          optsHtml += '<div class="color-swatch-image selected" data-opt-name="' + o.name + '" data-opt-value="' + o.value + '">'
            + (swatchImg ? '<img src="' + swatchImg + '" alt="' + o.value + '">' : '')
            + '<span class="jfk-tooltip">' + o.value + '</span>'
            + '</div>';
        } else {
          optsHtml += '<div class="text-block-6 selected" data-opt-name="' + o.name + '" data-opt-value="' + o.value + '">'
            + o.value + '<span class="jfk-tooltip">' + o.value + '</span>'
            + '</div>';
        }
        optsHtml += '</div>';
      });
    } else {
      var popts = Array.isArray(node.options) ? node.options : [];
      popts.forEach(function (o) {
        var availableVals = allowedOptValues[o.name] || [];
        if (!availableVals.length) return;
        var isColor = o.name.toLowerCase() === 'color';
        optsHtml += '<div class="detail-page-color">' + o.name + ':</div><div class="colors-div">';
        availableVals.forEach(function (val, i) {
          if (isColor) {
            var swatchImg = colorImageMap[val] || '';
            optsHtml += '<div class="color-swatch-image' + (i === 0 ? ' selected' : '') + '" data-opt-name="' + o.name + '" data-opt-value="' + val + '">'
              + (swatchImg ? '<img src="' + swatchImg + '" alt="' + val + '">' : '')
              + '<span class="jfk-tooltip">' + val + '</span>'
              + '</div>';
          } else {
            optsHtml += '<div class="text-block-6' + (i === 0 ? ' selected' : '') + '" data-opt-name="' + o.name + '" data-opt-value="' + val + '">'
              + val + '<span class="jfk-tooltip">' + val + '</span>'
              + '</div>';
          }
        });
        optsHtml += '</div>';
      });
    }

    var skuHtml = initialSku ? '<div class="store-brand-detail-page" id="jfk-modal-sku">SKU: ' + initialSku + '</div>' : '';

    var initialQty = isVariant
      ? node.quantityAvailable
      : (filteredVariants.length ? filteredVariants[0].node.quantityAvailable : null);
    var stockHtml = initialQty != null
      ? '<div class="stock-detail-page" id="jfk-modal-stock">Stock: only ' + initialQty + ' left in stock</div>'
      : '';

    // Build image slider HTML
    var slidesHtml = allImages.map(function (url, i) {
      return '<div class="jfk-modal-slide' + (i === 0 ? ' active' : '') + '">'
        + '<img src="' + url + '" alt="' + (title || '') + '">'
        + '</div>';
    }).join('');

    var arrowsHtml = allImages.length > 1
      ? '<div class="w-slider-arrow-left" id="jfk-slider-prev"><div class="icon-2 w-icon-slider-left"></div></div>'
        + '<div class="w-slider-arrow-right" id="jfk-slider-next"><div class="icon w-icon-slider-right"></div></div>'
      : '';

    var sliderHtml = '<div class="jfk-modal-slider">'
      + slidesHtml
      + arrowsHtml
      + '</div>';

    // Build right-panel details HTML
    var rightHtml = '<div class="store-brand-detail-page">' + (nodeGroupMap[nodeId] || currentStoreName || '') + '</div>'
      + '<div class="store-product-name-detail-page">' + (title || '') + '</div>'
      + '<div class="store-price-detailed-page">' + price + '</div>'
      + skuHtml
      + optsHtml
      + '<div id="kiwi-sizing-placeholder" style="margin-top:10px;margin-bottom:15px;"></div>'
      + '<div class="detail-page-color">Quantity</div>'
      + '<div class="div-block-38">'
        + '<div class="store-quantity">'
          + '<div class="div-block-37 jfk-qty-btn" data-action="minus"><div>-</div></div>'
          + '<div class="div-block-37 jfk-qty-value"><div>1</div></div>'
          + '<div class="div-block-37 jfk-qty-btn" data-action="plus"><div>+</div></div>'
        + '</div>'
      + '</div>'
      + stockHtml
      + '<a href="#" id="jfk-modal-add-to-cart" class="add-to-cart-button-product-detail-page w-button">Add to Cart</a>'
      + '<div class="tabs">'
        + '<div class="tabs-menu w-tab-menu">'
          + '<a href="#" class="tab-link-tab-1 w-inline-block active" data-tab="details">Details</a>'
        + '</div>'
        + '<div class="tabs-content w-tab-content">'
          + '<div class="product-description">' + (description || 'No description available.') + '</div>'
        + '</div>'
      + '</div>';

    currentNodeId = nodeId;

    var imagesEl  = el('jfk-modal-images');
    var detailsEl = el('jfk-modal-details');

    imagesEl.innerHTML  = sliderHtml;
    detailsEl.innerHTML = rightHtml;

    // Slider navigation
    if (allImages.length > 1) {
      var slides       = imagesEl.querySelectorAll('.jfk-modal-slide');
      var currentSlide = 0;
      function showSlide(idx) {
        Array.from(slides).forEach(function (s) { s.classList.remove('active'); });
        currentSlide = (idx + slides.length) % slides.length;
        slides[currentSlide].classList.add('active');
      }
      var prevBtn = el('jfk-slider-prev');
      var nextBtn = el('jfk-slider-next');
      if (prevBtn) prevBtn.addEventListener('click', function () { showSlide(currentSlide - 1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { showSlide(currentSlide + 1); });
    }

    // Option selection — text-block-6 (size etc.)
    Array.from(detailsEl.querySelectorAll('.text-block-6')).forEach(function (item) {
      item.addEventListener('click', function () {
        var optName = item.getAttribute('data-opt-name');
        Array.from(detailsEl.querySelectorAll('.text-block-6[data-opt-name="' + optName + '"]')).forEach(function (i) {
          i.classList.remove('selected');
        });
        item.classList.add('selected');
        updateVariantDisplay(node, detailsEl, filteredVariants);
      });
    });

    // Option selection — color swatches
    Array.from(detailsEl.querySelectorAll('.color-swatch-image')).forEach(function (swatch) {
      swatch.addEventListener('click', function () {
        var optName = swatch.getAttribute('data-opt-name');
        Array.from(detailsEl.querySelectorAll('.color-swatch-image[data-opt-name="' + optName + '"]')).forEach(function (s) {
          s.classList.remove('selected');
        });
        swatch.classList.add('selected');
        // Switch first slide image to this color's variant image
        var swatchImg = swatch.querySelector('img');
        if (swatchImg && swatchImg.src) {
          var slides = imagesEl.querySelectorAll('.jfk-modal-slide');
          Array.from(slides).forEach(function (s, i) { s.classList.toggle('active', i === 0); });
          var firstImg = slides[0] && slides[0].querySelector('img');
          if (firstImg) firstImg.src = swatchImg.src;
        }
        updateVariantDisplay(node, detailsEl, filteredVariants);
      });
    });

    // Qty buttons
    Array.from(detailsEl.querySelectorAll('.jfk-qty-btn')).forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var qtyDiv = detailsEl.querySelector('.jfk-qty-value div');
        var val = parseInt(qtyDiv.textContent, 10) || 1;
        if (btn.getAttribute('data-action') === 'plus') {
          qtyDiv.textContent = val + 1;
        } else if (val > 1) {
          qtyDiv.textContent = val - 1;
        }
      });
    });

    // Tab switching
    Array.from(detailsEl.querySelectorAll('[data-tab]')).forEach(function (tab) {
      tab.addEventListener('click', function (e) {
        e.preventDefault();
        Array.from(detailsEl.querySelectorAll('[data-tab]')).forEach(function (t) {
          t.classList.remove('active');
        });
        tab.classList.add('active');
      });
    });

    // Sold-out state
    var available = node.availableForSale !== false;
    var addBtn    = el('jfk-modal-add-to-cart');
    if (addBtn && !available) {
      addBtn.textContent         = 'Sold Out';
      addBtn.style.opacity       = '0.45';
      addBtn.style.pointerEvents = 'none';
    }

    // Add to Cart click
    if (addBtn) {
      addBtn.addEventListener('click', function (e) {
        e.preventDefault();
        var variantNumericId = getSelectedVariantId(node);
        if (!variantNumericId) return;
        var qty = getModalQty();
        var productNumericId = isVariant
          ? getNumericId((node.product && node.product.id) || node.id)
          : getNumericId(node.id);
        addToCart(variantNumericId, qty, productNumericId);
      });
    }

    el('jfk-product-modal').style.display = 'block';
    document.body.style.overflow = 'hidden';

    

    var productNode = isVariant ? node.product : node;
    var collectionIds = [];
    if (productNode && productNode.collections && productNode.collections.edges) {
      productNode.collections.edges.forEach(function (e) {
        if (e.node && e.node.id) {
          collectionIds.push(getNumericId(e.node.id));
        }
      });
    }
    var productData = {
      product: String(clickProductId),
      productID: String(clickProductId),
      title: title || '',
      tags: (productNode && productNode.tags) || [],
      type: (productNode && productNode.productType) || '',
      vendor: (productNode && productNode.vendor) || '',
      collections: collectionIds.join(','),
      images: allImages,
      options: (productNode && productNode.options) ? productNode.options.map(function(o) { return { name: o.name, values: o.values }; }) : [],
      variants: (productNode && productNode.variants && productNode.variants.edges) ? productNode.variants.edges.map(function(e) { return e.node; }) : []
    };

    loadKiwiSizing(productData);
  }

  function closeModal() {
    el('jfk-product-modal').style.display = 'none';
    document.body.style.overflow = '';
    currentModalVariants = [];
  }

  function loadKiwiSizing(productData) {
    var placeholder = el('kiwi-sizing-placeholder');
    if (!placeholder) return;

    if (window.ks && typeof window.ks.loadSizing === 'function') {
      window.ks.loadSizing({
        productData: productData,
        options: {
          el: placeholder,
          displayMode: 1,
          calculatorDisplayMode: 1
        },
        loaded: function (success) {
          console.log('[JFK] Kiwi Sizing loaded:', success);
        }
      });
    } else {
      var retries = 0;
      var interval = setInterval(function () {
        retries++;
        if (window.ks && typeof window.ks.loadSizing === 'function') {
          clearInterval(interval);
          window.ks.loadSizing({
            productData: productData,
            options: {
              el: placeholder,
              displayMode: 1,
              calculatorDisplayMode: 1
            },
            loaded: function (success) {
              console.log('[JFK] Kiwi Sizing loaded after retry:', success);
            }
          });
        } else if (retries >= 15) {
          clearInterval(interval);
          console.warn('[JFK] Kiwi Sizing (window.ks) was not found after retries.');
        }
      }, 200);
    }
  }




  /* ══════════════════════════════════════════════════════════════════════
     8. SEO
  ══════════════════════════════════════════════════════════════════════ */


  function updateSEO(store) {
    var name  = toPascalCase(store.name);
    var title = name + ' - ' + SHOP_NAME;
    var desc  = 'Shop ' + name + ' on ' + SHOP_NAME + '.';

    document.title = title;
    var titleEl = el('jfk-page-title');
    if (titleEl) titleEl.textContent = title;

    setMeta('jfk-meta-description',    desc);
    setMeta('jfk-og-title',            title);
    setMeta('jfk-og-description',      desc);
    setMeta('jfk-twitter-title',       title);
    setMeta('jfk-twitter-description', desc);

    if (store.logo) {
      setMeta('jfk-og-image',      store.logo);
      setMeta('jfk-twitter-image', store.logo);
    }
  }


  /* ══════════════════════════════════════════════════════════════════════
     9. CART
  ══════════════════════════════════════════════════════════════════════ */

  function getNumericId(gid) {
    if (!gid) return '';
    var decoded = gid;
    if (gid.indexOf('gid://') === -1) {
      try {
        decoded = atob(gid);
      } catch (e) {
        decoded = gid;
      }
    }
    return decoded.split('/').pop();
  }

  function getSelectedVariantId(node) {
    if (node.__typename === 'ProductVariant') return getNumericId(node.id);

    // Match selected options (text buttons + color swatches) to a variant
    var selected  = {};
    var detailsEl = el('jfk-modal-details');
    if (detailsEl) {
      Array.from(detailsEl.querySelectorAll('.text-block-6.selected')).forEach(function (item) {
        var name = item.getAttribute('data-opt-name');
        var val  = item.getAttribute('data-opt-value');
        if (name && val) selected[name] = val;
      });
      Array.from(detailsEl.querySelectorAll('.color-swatch-image.selected')).forEach(function (item) {
        var name = item.getAttribute('data-opt-name');
        var val  = item.getAttribute('data-opt-value');
        if (name && val) selected[name] = val;
      });
    }

    // Use the filtered variant list from the open modal (respects allowed variant IDs)
    var variants = currentModalVariants.length ? currentModalVariants : ((node.variants && node.variants.edges) || []);
    for (var i = 0; i < variants.length; i++) {
      var v    = variants[i].node;
      var opts = v.selectedOptions || [];
      var match = opts.length > 0 && opts.every(function (o) {
        return selected[o.name] === o.value;
      });
      if (match) return getNumericId(v.id);
    }
    // Fallback: first allowed variant
    return variants.length ? getNumericId(variants[0].node.id) : null;
  }

  function getModalQty() {
    var detailsEl = el('jfk-modal-details');
    var qtyEl     = detailsEl ? detailsEl.querySelector('.jfk-qty-value div') : null;
    return qtyEl ? (parseInt(qtyEl.textContent, 10) || 1) : 1;
  }

  /* ── Cart note ─────────────────────────────────────────────────────────
   * Single flag line written once:
   *   [JFK New Studio Store] https://admin.example.com/
   * Old [JFK]{...}[/JFK] JSON format is cleaned up on first run.
   * ------------------------------------------------------------------- */

  /* ── Cart item recovery ─────────────────────────────────────────────────
   * Tracks JFK-added items in localStorage. If the main office website
   * removes JFK items via a cart update, they are silently re-added when
   * the JFK page runs updateCartBadge. Items added > 4 hours ago are
   * dropped from the manifest (user likely intentionally removed them).
   * ------------------------------------------------------------------- */
  var JFK_MANIFEST_KEY  = 'jfk_manifest';
  var RESTORE_WINDOW_MS = 4 * 60 * 60 * 1000; // 4 hours
  var isRecovering      = false;

  function getManifest() {
    try { return JSON.parse(localStorage.getItem(JFK_MANIFEST_KEY) || '[]'); }
    catch (e) { return []; }
  }

  function saveManifest(items) {
    localStorage.setItem(JFK_MANIFEST_KEY, JSON.stringify(items));
  }

  function addToManifest(variantId, jfkProp, qty) {
    var items = getManifest();
    var vid   = String(variantId);
    var found = false;
    for (var i = 0; i < items.length; i++) {
      if (items[i].v === vid) {
        items[i].q += qty;
        items[i].t  = Date.now();
        found = true;
        break;
      }
    }
    if (!found) items.push({ v: vid, j: jfkProp, q: qty, t: Date.now() });
    saveManifest(items);
  }

  function recoverCartItems(cart) {
    if (isRecovering) return;
    var now      = Date.now();
    var manifest = getManifest();
    if (!manifest.length) return;

    // If cart is empty the user cleared it — wipe manifest
    if (!cart.item_count) { localStorage.removeItem(JFK_MANIFEST_KEY); return; }

    // Build set of _jfk variant IDs currently in cart
    var inCart = {};
    (cart.items || []).forEach(function (item) {
      if (item.properties && item.properties._jfk) {
        inCart[String(item.variant_id)] = true;
      }
    });

    // Entries still within restore window
    var fresh   = manifest.filter(function (e) { return (now - e.t) < RESTORE_WINDOW_MS; });
    var missing = fresh.filter(function (e) { return !inCart[e.v]; });

    // Drop expired entries
    if (fresh.length !== manifest.length) saveManifest(fresh);
    if (!missing.length) return;

    isRecovering = true;
    var idx = 0;
    function restoreNext() {
      if (idx >= missing.length) { isRecovering = false; updateCartBadge(); return; }
      var entry = missing[idx++];
      fetch('/cart/add.json', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(entry.v, 10), quantity: entry.q,
                               properties: { '_jfk': entry.j } })
      })
      .then(function (r) {
        if (!r.ok) {
          // Item likely out of stock — remove from manifest so we don't keep retrying
          var updated = getManifest().filter(function (e) { return e.v !== entry.v; });
          saveManifest(updated);
        }
        restoreNext();
      })
      .catch(function () { isRecovering = false; });
    }
    restoreNext();
  }

  function updateCartBadge() {
    fetch('/cart.json')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var badge = el('jfk-cart-count');
        if (badge) {
          var count = cart.item_count || 0;
          badge.textContent   = count;
          badge.style.display = count ? 'flex' : 'none';
        }
        recoverCartItems(cart);
      });
  }

  function addToCart(variantId, qty, productId) {
    var btn     = el('jfk-modal-add-to-cart');
    var jfkProp = currentStoreName + ' | ' + currentStoreId + ' | ' + (window.JFK_SESSION || '');
    if (btn) { btn.textContent = 'Adding\u2026'; btn.style.pointerEvents = 'none'; }

    fetch('/cart/add.json', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        id:         parseInt(variantId, 10),
        quantity:   qty,
        properties: { '_jfk': jfkProp }
      })
    })
    .then(function (r) {
      if (!r.ok) throw new Error('add failed');
      return r.json();
    })
    .then(function () {
      addToManifest(variantId, jfkProp, qty);
    })
    .then(function () {
      updateCartBadge();
      if (btn) { btn.textContent = 'Added!'; }
      setTimeout(function () {
        if (btn) { btn.textContent = 'Add to Cart'; btn.style.pointerEvents = ''; }
      }, 2000);
    })
    .catch(function () {
      if (btn) { btn.textContent = 'Error \u2014 try again'; btn.style.pointerEvents = ''; }
      setTimeout(function () {
        if (btn) { btn.textContent = 'Add to Cart'; btn.style.pointerEvents = ''; }
      }, 3000);
    });
  }


  /* ══════════════════════════════════════════════════════════════════════
     10. BOOT
  ══════════════════════════════════════════════════════════════════════ */

  function getStoreId() {
    var parts = window.location.pathname.split('/').filter(Boolean);
    var last  = parts[parts.length - 1];
    if (/^\d+$/.test(last)) return last;
    return new URLSearchParams(window.location.search).get('id');
  }

  function populateStore(data, storeId) {
    var store = data.store;
    currentStoreId   = String(storeId);
    currentStoreName = toPascalCase(store.name);

    var analyticsBase = API_BASE.replace(/\/stores\/?$/, '');
    fetch(analyticsBase + '/analytics/visit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        store_id:   currentStoreId,
        session_id: window.JFK_SESSION || null,
        referrer:   document.referrer || null
      })
    }).catch(function () {});

    updateSEO(store);
    loadHeaderFooter(data.settings);

    el('jfk-store-name').textContent = currentStoreName;

    var descEl = el('jfk-store-description');
    if (descEl) {
      if (store.description) {
        descEl.innerHTML = store.description;
        descEl.style.display = 'block';
      } else {
        descEl.style.display = 'none';
      }
    }

    var logoEl = el('jfk-store-logo');
    logoEl.alt = currentStoreName;
    var logoUrl = resolveLogoUrl(store.logo);
    logoEl.onerror = function () {
      logoEl.onerror = null;
      if (FALLBACK_IMAGE) logoEl.src = FALLBACK_IMAGE;
    };
    logoEl.src = logoUrl || FALLBACK_IMAGE;

    // Only active product groups
    storeProductGroups = (store.productgroups || [])
      .filter(isActive)
      .map(function (group) {
        return Object.assign({}, group, {
          products: (group.products || [])
        });
      });
    initProductGroups(storeProductGroups);

    hideEl('jfk-store-loading');
    showEl('jfk-store-content');
  }

  function init() {
    // Modal close bindings
    var modalClose = el('jfk-modal-close');
    var modal      = el('jfk-product-modal');
    if (modalClose) modalClose.addEventListener('click', closeModal);
    if (modal) modal.addEventListener('click', function (e) {
      if (e.target === modal) closeModal();
    });
    document.addEventListener('keydown', function (e) {
      if ((e.key === 'Escape' || e.keyCode === 27) && modal && modal.style.display !== 'none') {
        closeModal();
      }
    });

    var storeId = getStoreId();
    if (!storeId) {
      hideEl('jfk-store-loading');
      showEl('jfk-store-error');
      return;
    }

    updateCartBadge();

    // Re-check cart (and recover any removed JFK items) whenever the user
    // switches back to this tab from the main office or any other tab.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) updateCartBadge();
    });

    fetchStore(storeId)
      .then(function (json) {
        if (json.success && json.data && json.data.store) {
          var store = json.data.store;
          if (store.status === 0 || store.status === '0') {
            hideEl('jfk-store-loading');
            showEl('jfk-store-inactive');
          } else {
            populateStore(json.data, storeId);
          }
        } else {
          throw new Error('not found');
        }
      })
      .catch(function () {
        hideEl('jfk-store-loading');
        showEl('jfk-store-error');
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
